import { createFileRoute } from "@tanstack/react-router";
import { DocPage } from "@/components/DocPage";

export const Route = createFileRoute("/platform-policies")({
  head: () => ({
    meta: [
      { title: "Platform Policies — Want To Buy" },
      { name: "description", content: "Rules for using the Want To Buy connection platform: the mandatory 2-photo scam filter, member behaviour, and enforcement." },
      { property: "og:title", content: "Platform Policies — Want To Buy" },
      { property: "og:description", content: "Rules for using the Want To Buy connection platform: the mandatory 2-photo scam filter, member behaviour, and enforcement." },
      { name: "twitter:title", content: "Platform Policies — Want To Buy" },
      { name: "twitter:description", content: "Rules for using the Want To Buy connection platform: the mandatory 2-photo scam filter, member behaviour, and enforcement." },
    ],
  }),
  component: PlatformPoliciesPage,
});

export function PlatformPoliciesContent() {
  return (
    <>
      <p className="!mt-0 text-xs uppercase tracking-wider text-muted-foreground">Last updated: July 2026</p>

      <section className="panel-note">
        <p className="!mt-0">
          <strong>Want To Buy is a connection platform only.</strong> Its sole function is to connect TCG buyers with sellers who have the cards those buyers are looking for. The platform does not process payments, hold funds, verify card authenticity, or mediate disputes. The only enforced platform rule is the mandatory 2-photo scam filter described in section 2. Everything else on this page is either a platform-integrity rule or a strong recommendation.
        </p>
      </section>

      <section>
        <h2>1. Prohibited conduct</h2>
        <ul>
          <li>Users may not manipulate pricing through coordinated offers or fake buy orders.</li>
          <li>Users may not interfere with another user's transactions.</li>
          
          <li>Users may not post buy orders for prohibited items including counterfeit cards, stolen goods, or items whose sale is restricted by law.</li>
          <li>Users may not create multiple accounts to circumvent suspensions or bans.</li>
          <li>Users may not engage in shill offers or otherwise artificially inflate offer counts or prices.</li>
        </ul>
        <h3>1a. Counterfeit, altered &amp; misrepresented items</h3>
        <p>Counterfeit, altered, resealed, tampered, or intentionally misrepresented items are strictly prohibited. Confirmed counterfeit sales result in immediate and permanent account ban.</p>
      </section>

      <section>
        <h2>2. Mandatory 2-photo scam filter (enforced)</h2>
        <p>
          Before a buyer and seller are connected through the message thread, the seller must submit <strong>exactly 2 timestamped photos</strong>:
        </p>
        <ul>
          <li><strong>Photo 1</strong> — front of the card with a handwritten note in the same frame.</li>
          <li><strong>Photo 2</strong> — back of the card with a handwritten note in the same frame.</li>
        </ul>
        <p>Each handwritten note must contain, written by hand:</p>
        <ul>
          <li>The current date in <strong>DD/MM/YYYY</strong> format.</li>
          <li><code>Seller:</code> followed by the seller's exact Want To Buy username.</li>
          <li><code>Buyer:</code> followed by the buyer's exact Want To Buy username.</li>
          <li>The full order ID.</li>
          <li>The card condition.</li>
        </ul>
        <p>
          For graded cards, one of the two photos must additionally show the slab label with the cert number clearly legible alongside the handwritten note.
        </p>
        <p>
          <strong>The mandatory 2-photo requirement is the one non-negotiable platform rule.</strong> It exists solely as a scam-prevention filter — it proves the seller physically possesses the card at the time of the transaction. Want To Buy does <strong>not</strong> authenticate cards, grade cards, or verify condition claims from these photos. The buyer is solely responsible for evaluating the photos and deciding whether to proceed. Sellers who cannot or will not submit these photos cannot proceed. After reviewing them, buyers may optionally request additional photos through the message thread — sellers have <strong>24 hours</strong> to respond.
        </p>
      </section>

      <section>
        <h2>3. Off-platform communication</h2>
        <p>
          Want To Buy charges no fees and has no revenue tied to keeping conversations on the platform. Users are free to share personal contact information — phone numbers, email addresses, social handles, external links, or payment references — and to continue the conversation on any channel they choose.
        </p>
        <p>
          Want To Buy still strongly recommends completing payment through <strong>PayPal Goods &amp; Services</strong> because it provides independent buyer and seller protection. Moving payment off PayPal Goods &amp; Services is your choice and forfeits that protection.
        </p>
      </section>

      <section>
        <h2>4. Rating and feedback integrity</h2>
        <p>Ratings must reflect genuine first-hand experience of a completed transaction. Feedback extortion, coordinated rating manipulation, and retaliatory ratings unrelated to the transaction are prohibited and may be removed.</p>
      </section>

      <section>
        <h2>5. Member behaviour</h2>
        <p>All users must treat each other with respect in all platform communications. Threats, harassment, hate speech, spam, and impersonation are prohibited. Messages are stored and may be reviewed by administrators for policy compliance.</p>
      </section>

      <section>
        <h2>6. Identity verification data (Verified Store program)</h2>
        <p>
          Documents submitted as part of the optional Store Verification program are stored encrypted, accessed only by authorised administrators, used solely to verify identity, and never shared with other users or third parties. In accordance with Québec Law 25, uploaded verification documents are automatically scheduled for deletion within 90 days of the application's resolution.
        </p>
      </section>

      <section>
        <h2>7. Payments — recommendation, not enforcement</h2>
        <p>
          Want To Buy does not process, hold, or release payments. Payments are arranged entirely between buyer and seller after they are connected. <strong>Want To Buy strongly recommends PayPal Goods &amp; Services</strong> for every transaction because PayPal provides independent buyer and seller protection through their own terms.
        </p>
        <p>
          Transactions completed via PayPal Friends &amp; Family, bank transfer, cryptocurrency, or any method other than PayPal Goods &amp; Services are <strong>not covered</strong> by PayPal's protection program. Want To Buy provides no protection of its own.
        </p>
      </section>

      <section>
        <h2>8. Enforcement</h2>
        <p>Want To Buy may take the following actions for platform policy violations:</p>
        <ol>
          <li>Formal warning.</li>
          <li>Temporary posting restriction.</li>
          <li>Account suspension pending review.</li>
          <li>Permanent ban.</li>
        </ol>
        <p>
          Severe violations — confirmed counterfeit sales, ban evasion, and confirmed fraud — bypass the warning system and result in immediate permanent ban. Enforcement decisions are logged in the internal admin audit trail.
        </p>
        <p>
          Enforcement is limited to platform-integrity actions (warnings, restrictions, suspensions, and bans). Want To Buy does not mediate the outcome of individual transactions between users.
        </p>
      </section>
    </>
  );
}

function PlatformPoliciesPage() {
  return (
    <DocPage
      eyebrow="Platform Rules"
      title="Platform Policies"
      intro="The rules that keep Want To Buy usable as a connection platform. Only one — the mandatory 2-photo scam filter — is enforced against transactions. The rest govern behaviour on the platform itself."
    >
      <PlatformPoliciesContent />
    </DocPage>
  );
}
